import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  forwardRef,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Station } from '@models/responses/station-response.model';
import {
  ControlContainer,
  ControlValueAccessor,
  FormControl,
  FormGroup,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';
import { filter, takeUntil } from 'rxjs/operators';
import { Subject, take } from 'rxjs';
import { NullableString } from '@models/app.models';
import { FeatureFlagService } from '@services/feature-flag/feature-flag.service';
import { FeatureFlagsEnum } from '@models/environment.type';
import { TripType } from '@constants/home.constants';
import {selectNearestStation} from '@selectors/station.selector';
import { Store } from '@ngrx/store';
import { StationPickerModalComponent } from '@components/modals/station-picker-modal/station-picker-modal.component';
import { ModalService } from '@services/modal/modal.service';
import { BsModalRef, ModalModule } from 'ngx-bootstrap/modal';
import { selectShouldUseMobileStyling } from '@selectors/app.selector';
import { DOHOP_BY_CULTURE } from '@constants/dohop.constants';
import { CultureKeyType } from '@constants/app.constants';
import { DohopService } from '@services/api-clients/dohop.service';
import { GrowthBookService } from '@services/growthbook/growthbook.service';
import { GrowthBookFeatureKeys } from '@models/growthbook.models';
import { SearchWidgetCopy } from '@models/cms/search-widget.model';
import { PromoCodesService } from '@services/pormo-codes/promo-codes.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TolstoyService } from "@services/tolstoy/tolstoy.service";
import {selectStationsFromDestinationMap} from "@selectors/availability.selector";
import {
  ConfirmationMessageModalComponent
} from "@components/modals/confirmation-message-modal/confirmation-message-modal.component";

@Component({
  selector: 'app-station-picker',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, ModalModule],
  templateUrl: './station-picker.component.html',
  styleUrls: ['./station-picker.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(
        /* istanbul ignore next */
        () => StationPickerComponent,
      ),
      multi: true,
    },
  ],
})
export class StationPickerComponent
  implements OnInit, OnDestroy, ControlValueAccessor
{
  private destroyRef = inject(DestroyRef);
  originStations: Station[] = [];
  destinationStations: Station[] = [];

  @Input() isDisabled: boolean;
  @Input() stations: Station[] | null | undefined;
  @Input() norseStations: Station[] | null | undefined;
  @Input() dohopFeatureStations: Station[] | null | undefined;
  @Input() tripType: TripType | null | undefined;
  @Input() index = 0;
  @Input() copy?: SearchWidgetCopy;
  @Input() fromCopy: string;
  @Input() toCopy: string;
  @Input() stationsForceMacCode: string[] = [];
  @Input() culture: CultureKeyType;
  @Input() isPromoCodeLanding: boolean = false;
  @Input() isDateRangePickerEnabled: boolean = false;

  @Output() originStationChange = new EventEmitter();
  @Output() destinationStationChange = new EventEmitter();

  nearestStation$ = this.store.select(selectNearestStation);
  shouldUseMobileStyling$ = this.store.select(selectShouldUseMobileStyling);
  stationsFromDestinationMap$ = this.store.select(selectStationsFromDestinationMap);

  get originStationNameControl(): FormControl {
    return this.stationsFormGroup?.get('originStationName') as FormControl;
  }

  get originStationCodeControl(): FormControl {
    return this.stationsFormGroup?.get('originStationCode') as FormControl;
  }

  get destinationStationNameControl(): FormControl {
    return this.stationsFormGroup?.get('destinationStationName') as FormControl;
  }

  get destinationStationCodeControl(): FormControl {
    return this.stationsFormGroup?.get('destinationStationCode') as FormControl;
  }

  value: unknown;
  changed: (value: string) => void;
  touched: () => void;
  stationsWithMatchingMarkets: Station[] | null = null;
  routeSelectorBorderRadius = '';
  originPickerVisible = false;
  destinationPickerVisible = false;
  showDohopList = false;
  stationsFormGroup: FormGroup;
  onDestroy$ = new Subject<void>();

  stationPickerModalRef?: BsModalRef<StationPickerModalComponent>;

  readonly FeatureFlagsEnum = FeatureFlagsEnum;
  readonly gbf = GrowthBookFeatureKeys;

  constructor(
    public modalService: ModalService,
    public featureFlagService: FeatureFlagService,
    private dohopService: DohopService,
    private controlContainer: ControlContainer,
    private changeDetector: ChangeDetectorRef,
    public store: Store,
    private gbs: GrowthBookService,
    private promoCodesService: PromoCodesService,
    private readonly tolstoyService: TolstoyService
  ) {}

  ngOnInit(): void {
    this.stationsFormGroup = this.controlContainer.control as FormGroup;

    this.nearestStation$
      .pipe(
        filter((station) => !!station),
        takeUntil(this.onDestroy$),
      )
      .subscribe((nearestStation) => {
        if (
          this.originStationCodeControl.value ||
          this.originStationNameControl.value
        ) {
          return;
        }
        /* eslint-disable @typescript-eslint/no-non-null-assertion */
        this.onOriginStationSelect(nearestStation!);
        this.originStationChange.emit({
          stationName: nearestStation!.fullName,
          stationCode: nearestStation!.stationCode,
        });
        /* eslint-enable @typescript-eslint/no-non-null-assertion */
      });

    // needed only for exp-001996-destinations-map-redesign
    this.stationsFromDestinationMap$
      .pipe(
        takeUntil(this.onDestroy$),
      )
      .subscribe((stations) => {
        if (stations) {
          const origin = this.stations?.find(el => el.stationCode === stations[0])
          const destination = this.stations?.find(el => el.stationCode === stations[1])
          if (origin && destination) {
            this.onOriginStationSelect(origin);
            this.originStationChange.emit({
              stationName: origin.fullName,
              stationCode: origin.stationCode,
            });
            this.onDestinationStationSelect(destination);
            this.destinationStationChange.emit({
              stationName: destination.fullName,
              stationCode: destination.stationCode
            });
          }
        }
      });

    this.originStations = this.stations ?? [];
    if (this.originStations) {
      this.originStations = this.originStations.filter((s) => !s.isMac) ?? [];
    }

    this.originStationCodeControl.valueChanges
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((value) => {
        if (this.stationsForceMacCode.includes(<string>value)) {
          const selectedStation = this.stations?.find(
            (s) => s.stationCode === value,
          );
          const macStation = this.stations?.find(
            (s) => s.stationCode === selectedStation?.macCode,
          );
          // the order we set name and code matters here. related to forcing MAC code
          this.originStationNameControl.setValue(macStation?.fullName);
          this.originStationCodeControl.setValue(macStation?.stationCode);
        } else {
          this.originStationChange.emit({
            stationName: <string>this.originStationNameControl.value,
            stationCode: <string>value ?? '',
          });
          this.updateStations();
        }
      });

    this.destinationStationCodeControl.valueChanges
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((value) => {
        if (this.stationsForceMacCode.includes(<string>value)) {
          const selectedStation = this.stations?.find(
            (s) => s.stationCode === value,
          );
          const macStation = this.stations?.find(
            (s) => s.stationCode === selectedStation?.macCode,
          );
          // the order we set name and code matters here. related to forcing MAC code
          this.destinationStationNameControl.setValue(macStation?.fullName);
          this.destinationStationCodeControl.setValue(macStation?.stationCode);
        } else {
          this.destinationStationChange.emit({
            stationName: <string>this.destinationStationNameControl.value,
            stationCode: <string>value ?? '',
          });

          this.updateStations(false);
        }
      });
    this.promoCodesService.onChangeActivePromoDeal()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((deal) => {
        if (deal) {
          this.originStationNameControl.setValue('');
          this.originStationCodeControl.setValue('');
          this.destinationStationNameControl.setValue('');
          this.destinationStationCodeControl.setValue('');
          if (deal.from) {
            const finded = this.stations?.find(station => station.stationCode === deal.from)
            if(finded) this.onOriginStationSelect(finded)
          }
          if (deal.to) {
            const finded = this.stations?.find(station => station.stationCode === deal.to)
            if (finded) this.onDestinationStationSelect(finded)
          }
        }
      })
  }

  getMacOverride(code: NullableString): string {
    if (code == 'JM1' || code == 'FL1' || code == 'SF1') {
      return 'ALL';
    }
    return code ?? '';
  }

  getDestinationStations(originStationCode: NullableString): Station[] {
    const results = this.stations ?? [];

    return results
      .filter(
        (s) =>
          s.validMarkets?.some(
            (m) =>
              m.stationCode === originStationCode &&
              (this.featureFlagService.isFeatureActive(
                FeatureFlagsEnum.CONNECTIONS,
              ) &&
              this.gbs.growthBook.isOn(this.gbf.FEAT_US366_DOHOP_V2_TOP_CONNECTIONS) &&
              ['b', 'c'].includes(this.gbs.growthBook.getFeatureValue(
                this.gbf.FEAT_US366_DOHOP_V2_TOP_CONNECTIONS,
                ''
              ))
                ? true
                : !m.isDohop),
          ),
      )
      .filter((s) => !s.isMac)
      .sort((a, b) =>
        a.fullName > b.fullName ? 1 : a.fullName < b.fullName ? -1 : 0,
      );
  }

  /**
   * Updates the stations observables based on the user
   * selections. Filters out which selections can be made.
   * @param journeyKey The journey key
   */
  updateStations(originCode: boolean = true): void {
    let originStationCode = this.originStationCodeControl
      ?.value as NullableString;

    if (!originCode) {
      originStationCode = this.destinationStationCodeControl
        ?.value as NullableString;
    }
    this.destinationStations = this.getDestinationStations(originStationCode);
    this.changeDetector.detectChanges();
  }

  // ngOnChanges(changes: SimpleChanges): void {
  //   if (changes?.stations?.currentValue) {
  //     this.updateStations();
  //   }
  // }

  /* istanbul ignore next */
  writeValue(value: unknown): void {
    this.value = value;
  }

  /* istanbul ignore next */
  registerOnChange(fn: () => void): void {
    this.changed = fn;
  }

  /* istanbul ignore next */
  registerOnTouched(fn: () => void): void {
    this.touched = fn;
  }

  onOriginStationSelect(station: Station): void {
    if (this.isDisabled) {
      return;
    }
    // the order we set name and code matters here. related to forcing MAC code
    this.originStationNameControl.setValue(station.fullName);
    this.originStationCodeControl.setValue(station.stationCode);
    this.originPickerVisible = false;
    this.routeSelectorBorderRadius = '';
  }

  onDestinationStationSelect(station: Station): void {
    if (this.isDisabled) {
      return;
    }
    // the order we set name and code matters here. related to forcing MAC code
    this.destinationStationNameControl.setValue(station.fullName);
    this.destinationStationCodeControl.setValue(station.stationCode);
    this.destinationPickerVisible = false;
    this.routeSelectorBorderRadius = '';
  }

  isElement(target: EventTarget | null): target is HTMLElement {
    return !!target && 'className' in target;
  }

  isDohopRoute(station: Station, selectedStationCode: string): boolean {
    if (station.validMarkets && selectedStationCode !== '') {
      return (
        station.validMarkets.find((m) => m.stationCode === selectedStationCode)
          ?.isDohop ?? false
      );
    }

    return false;
  }

  openStationPickerForOriginInput() {
    if (this.gbs.isVariantOfExperimentEnabled('feat-002315-destination-selection-before-origin', 'b')) {
      if (this.emptyOrFullyFilled() || this.destinationStationCodeControl.value === '') {
        this.onOriginStationClick()
      } else {
        this.onDestinationStationClick(false);
      }
    } else {
      this.onOriginStationClick()
    }
  }

  openStationPickerForDestinationInput() {
    if (this.gbs.isVariantOfExperimentEnabled('feat-002315-destination-selection-before-origin', 'b')) {
      if (this.emptyOrFullyFilled() || this.originStationCodeControl.value === '') {
        this.onOriginStationClick(false)
      } else {
        this.onDestinationStationClick();
      }
    } else {
      this.onDestinationStationClick()
    }
  }

  emptyOrFullyFilled(): boolean {
    return (this.destinationStationCodeControl.value === '' && this.originStationCodeControl.value === '') || (!!this.destinationStationCodeControl.value && !!this.originStationCodeControl.value)
  }

  onOriginStationClick(isOrigin: boolean = true): void {
    if (this.isDisabled) {
      return;
    }
    if (this.gbs.isVariantOfExperimentEnabled('feat-002315-destination-selection-before-origin', 'b') && this.emptyOrFullyFilled()) {
      this.destinationStationNameControl.setValue('');
      this.destinationStationCodeControl.setValue('');
      this.originStationNameControl.setValue('');
      this.originStationCodeControl.setValue('');
    }

    this.originPickerVisible = !this.originPickerVisible;
    this.destinationPickerVisible = false;
    if (this.originPickerVisible) {
      this.destinationStationNameControl.setValue('');
      this.destinationStationCodeControl.setValue('');
      this.updateStations();

      this.tolstoyService.hideTolstoyVideo();

      this.stationPickerModalRef = this.modalService.show(
        StationPickerModalComponent,
        {
          class: 'modal-dialog-fullscreen modal-desktop-600',
          initialState: {
            isOrigin: true,
            dohopFeaturedStations: this.featureFlagService.isFeatureActive(
              FeatureFlagsEnum.CONNECTIONS,
            )
              ? DOHOP_BY_CULTURE[this.culture]
              : null,
            stations: this.originStations,
            shouldUseMobileStyling: this.shouldUseMobileStyling$,
            copy: this.copy,
          },
        },
      );

      this.stationPickerModalRef.onHide?.pipe(take(1))
        .subscribe(() => {
          this.tolstoyService.showTolstoyVideo();
          this.originPickerVisible = false;
        })

      this.stationPickerModalRef.content?.stationChange
        .pipe(take(1))
        .subscribe((station) => {
          /* istanbul ignore else */
          if (!station) return;


          if (this.gbs.isVariantOfExperimentEnabled('feat-002315-destination-selection-before-origin', 'b')) {
            if (isOrigin) {
              this.onOriginStationSelect(station);
            } else {
              this.onDestinationStationSelect(station);
            }
          } else {
            this.onOriginStationSelect(station);
          }

          this.stationPickerModalRef?.hide();
        });
    }
  }

  async onDestinationStationClick(isDestination: boolean = true): Promise<void> {
    if (this.isDisabled) {
      return;
    }

    if (this.gbs.isVariantOfExperimentEnabled('feat-002315-destination-selection-before-origin', 'b') && this.emptyOrFullyFilled()) {
      this.destinationStationNameControl.setValue('');
      this.destinationStationCodeControl.setValue('');
      this.originStationNameControl.setValue('');
      this.originStationCodeControl.setValue('');
    }

    if (this.gbs.isVariantOfExperimentEnabled('feat-002315-destination-selection-before-origin', 'b')) {
      this.destinationPickerVisible = true;
    } else {
      if (!this.originStationNameControl.value) {
        this.destinationPickerVisible = false;
        this.modalService.show(ConfirmationMessageModalComponent, {
          class: 'modal-desktop-600',
          initialState: {
            titleText: this.copy?.selectOriginFirst,
            messageText: this.copy?.selectOriginFirstCopy,
            confirmBtnText: this.copy?.iUnderstand,
            extraTitleClasses: 'section-title--big',
            extraConfirmBtnClasses: 'btn--green btn--full-width',
          }
        })
      } else {
        this.destinationPickerVisible = true;
      }
    }
    this.originPickerVisible = false;
    if (this.destinationPickerVisible) {
      let dohopStationCodes: string[] | null = null;
      if (
        this.featureFlagService.isFeatureActive(FeatureFlagsEnum.CONNECTIONS) &&
        this.gbs.growthBook.isOn(this.gbf.FEAT_US366_DOHOP_V2_TOP_CONNECTIONS) &&
        ['b', 'c'].includes(this.gbs.growthBook.getFeatureValue(
          this.gbf.FEAT_US366_DOHOP_V2_TOP_CONNECTIONS,
          ''
        ))
      ) {
        let origin: NullableString = this.originStationCodeControl.value as NullableString;
        if (this.gbs.isVariantOfExperimentEnabled('feat-002315-destination-selection-before-origin', 'b')) {
          if (!isDestination) {
            origin = this.destinationStationCodeControl.value as NullableString;
          }
        }
        await this.dohopService
          .getPickerStations({
            prefix: '',
            origin
          })
          .pipe(
            filter((response) => !!response),
            take(1),
          )
          .subscribe((response) => {
            dohopStationCodes = response.data;
            this.showDestinationModal(dohopStationCodes, isDestination);
          });
      } else {
        this.showDestinationModal(dohopStationCodes, isDestination);
      }
    }
  }

  showDestinationModal(dohopStationCodes: string[] | null, isDestination: boolean): void {
    this.updateStations(isDestination);

    this.tolstoyService.hideTolstoyVideo();

    let originStationCode: NullableString = this.originStationCodeControl.value as NullableString;
    if (this.gbs.isVariantOfExperimentEnabled('feat-002315-destination-selection-before-origin', 'b')) {
      if (!isDestination) {
        originStationCode = this.destinationStationCodeControl.value as NullableString;
      }
    }

    this.stationPickerModalRef = this.modalService.show(
      StationPickerModalComponent,
      {
        class: 'modal-desktop-600',
        initialState: {
          isDestination: true,
          originStationCode,
          stations: this.destinationStations,
          dohopFeaturedStations: dohopStationCodes,
          shouldUseMobileStyling: this.shouldUseMobileStyling$,
          copy: this.copy,
        },
      },
    );

    this.stationPickerModalRef.onHide?.pipe(take(1))
      .subscribe(() => {
        this.tolstoyService.showTolstoyVideo();
        this.destinationPickerVisible = false;
      })

    this.stationPickerModalRef.content?.stationChange
      .pipe(take(1))
      .subscribe((station: Station) => {
        /* istanbul ignore else */
        if (!station) return;

        if (this.gbs.isVariantOfExperimentEnabled('feat-002315-destination-selection-before-origin', 'b')) {
          if (isDestination) {
            this.onDestinationStationSelect(station);
          } else {
            this.onOriginStationSelect(station);
          }
        } else {
          this.onDestinationStationSelect(station);
        }


        this.stationPickerModalRef?.hide();
      });
  }

  onOriginKeydown(): void {
    this.routeSelectorBorderRadius = 'borderRadiusBottomLeftOff';
    this.originPickerVisible = true;
    this.destinationPickerVisible = false;
  }

  onDestinationKeydown(): void {
    this.routeSelectorBorderRadius = 'borderRadiusBottomRightOff';
    this.destinationPickerVisible = true;
    this.originPickerVisible = false;
  }

  onStationsMouseup(
    target: HTMLInputElement,
    partnerValue: string | null | undefined,
  ): void {
    if (this.isDisabled) {
      return;
    }

    target.setSelectionRange(0, target.value.length);

    if (this.featureFlagService.isFeatureActive(FeatureFlagsEnum.CONNECTIONS)) {
      this.showDohopList =
        !this.tripType || this.tripType === TripType.multiCity
          ? false
          : !partnerValue;
    }
  }

  onStationsTabKeyDown(id: string): void {
    if (this.isDisabled) {
      return;
    }
    if (id === 'Origin') {
      this.originPickerVisible = false;
      this.routeSelectorBorderRadius = 'borderRadiusBottomRightOff';
      if (this.originStations.length === 1) {
        this.originStationNameControl.setValue(
          this.originStations[0]?.fullName,
        );
        this.originStationCodeControl.setValue(
          this.originStations[0]?.stationCode,
        );
      }
      this.destinationPickerVisible = true;
    }
    if (id === 'Destination') {
      this.originPickerVisible = false;
      this.destinationPickerVisible = false;
      this.routeSelectorBorderRadius = '';
      if (this.destinationStations.length === 1) {
        this.destinationStationNameControl.setValue(
          this.destinationStations[0]?.fullName,
        );
        this.destinationStationCodeControl.setValue(
          this.destinationStations[0]?.stationCode,
        );
      }
    }
  }

  onSwapClick(): void {
    if (this.isDisabled) {
      return;
    }
    if (
      this.originStationNameControl.value ||
      this.destinationStationNameControl.value
    ) {
      const tempOriginStationName = <string>this.originStationNameControl.value;
      const tempOriginStationCode = <string>this.originStationCodeControl.value;

      this.originStationNameControl.setValue(
        this.destinationStationNameControl.value,
      );
      this.originStationCodeControl.setValue(
        this.destinationStationCodeControl.value,
      );

      this.destinationStationNameControl.setValue(tempOriginStationName);
      this.destinationStationCodeControl.setValue(tempOriginStationCode);
    }
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
  }
}
